import React from "react";
import styled from "styled-components";
import { Outlet, Link, NavLink, Navigate, useLocation } from "react-router";
import { Container, Navbar, Nav, NavDropdown } from "react-bootstrap";
import { LogOut } from "react-feather";
import { useAuth, usePermissions } from "../hooks";
import {
  PermissionAction,
  PermissionResource,
  SystemGroup,
} from "../interfaces";

const StyledNavbar = styled(Navbar)`
  margin-bottom: 20px;
  border-radius: 0.25rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
`;

const StyledNavLink = styled(NavLink)`
  &.active {
    font-weight: bold;
  }
`;

const StyledNavbarBrand = styled(Navbar.Brand)`
  font-weight: bold;
  text-decoration: none;
`;

const LogoutLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const Layout: React.FC = () => {
  const { isAuthenticated, isLoading, logout } = useAuth();
  const { can, isAdmin, permissions } = usePermissions();
  const location = useLocation();
  const isQaReviewRoute = /^\/qa-lists\/[^/]+$/.test(location.pathname);

  const canAccessResource = (resource: PermissionResource): boolean =>
    isAdmin ||
    can(PermissionAction.ReadAny, resource) ||
    can(PermissionAction.CreateAny, resource) ||
    can(PermissionAction.UpdateAny, resource) ||
    can(PermissionAction.DeleteAny, resource);

  const canAccessKindredData = canAccessResource(
    PermissionResource.KindredData
  );
  const canAccessFullAutoCouponsTargeting = canAccessResource(
    PermissionResource.FullAutoCouponsTargeting
  );
  const canAccessQaLists = canAccessResource(PermissionResource.QaList);
  const canAccessCouponBulkUpdate = canAccessResource(
    PermissionResource.CouponBulkUpdate
  );
  const canAccessDomainReview = canAccessResource(
    PermissionResource.DomainReview
  );
  const canAccessOpenData = canAccessResource(PermissionResource.OpenData);
  const canAccessSmartShopping = canAccessResource(PermissionResource.SmartShopping);
  const canAccessPartners = canAccessResource(PermissionResource.Partners);
  const canAccessWorkPipeline = canAccessResource(
    PermissionResource.WorkPipeline
  );
  const canAccessAffiliateProducts = canAccessResource(
    PermissionResource.AffiliateProduct
  );
  const canAccessBasketTesting = canAccessResource(
    PermissionResource.BasketTesting
  );
  const canAccessOps =
    canAccessKindredData ||
    canAccessAffiliateProducts ||
    canAccessBasketTesting ||
    canAccessResource(PermissionResource.Task) ||
    canAccessResource(PermissionResource.Merchant);

  const canManageGroups =
    isAdmin ||
    can(PermissionAction.CreateAny, PermissionResource.GroupMembership) ||
    can(PermissionAction.DeleteAny, PermissionResource.GroupMembership);
  const canManageUsers =
    isAdmin || permissions?.groups.includes(SystemGroup.QaManagers);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // Show loading state
  if (isLoading) {
    return null; // Or a loading spinner if you prefer
  }

  // If not authenticated and not on login page, redirect to login
  if (!isAuthenticated && location.pathname !== "/login") {
    return <Navigate to="/login" replace />;
  }

  // If authenticated and on login page, redirect to home
  if (isAuthenticated && location.pathname === "/login") {
    return <Navigate to="/" replace />;
  }

  // If not authenticated, show outlet without navbar (for login page)
  if (!isAuthenticated) {
    return <Outlet />;
  }

  // If authenticated, show full layout with navbar
  return (
    <Container
      fluid={isQaReviewRoute}
      className={isQaReviewRoute ? "px-3" : undefined}
    >
      <StyledNavbar bg="light" expand="lg">
        <Container fluid>
          <StyledNavbarBrand as={Link} to="/">
            <i className="bi bi-robot"></i> Agent AI
          </StyledNavbarBrand>
          <Navbar.Toggle aria-controls="navbarNav" />
          <Navbar.Collapse id="navbarNav">
            <Nav className="me-auto">
              <NavDropdown title="Agentic" id="agentic-dropdown">
                <NavDropdown.Item as={Link} to="/agentic/dashboard">
                  Dashboard
                </NavDropdown.Item>
                {canAccessOps && (
                  <>
                    <NavDropdown.Item as={Link} to="/merchants">
                      Merchants
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to="/tasks">
                      Tasks
                    </NavDropdown.Item>
                  </>
                )}
                <NavDropdown.Divider />
                <NavDropdown.Item as={Link} to="/test-results">
                  Test Results
                </NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/coupon-results">
                  Coupon Results
                </NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/coupon-performance">
                  Coupon Performance
                </NavDropdown.Item>
                <NavDropdown.Item
                  as={Link}
                  to="/auto-coupons/template-results"
                >
                  Template Results
                </NavDropdown.Item>
              </NavDropdown>
              {canAccessOps && (
                <NavDropdown title="Templates" id="templates-dropdown">
                  <NavDropdown.Item as={Link} to="/auto-coupons/templates">
                    Templates
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/auto-coupons/evaluations">
                    Evaluations
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/auto-coupons/runs">
                    Runs
                  </NavDropdown.Item>
                  <NavDropdown.Item
                    as={Link}
                    to="/auto-coupons/session-merchants"
                  >
                    Session Merchants
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/auto-coupons/session-runs">
                    Session Runs
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/auto-coupons/merchant-map-editor">
                    Merchant map editor
                  </NavDropdown.Item>
                </NavDropdown>
              )}
              {(canAccessKindredData ||
                canAccessFullAutoCouponsTargeting ||
                canAccessQaLists) && (
                <NavDropdown title="Kindred Live" id="kindred-live-dropdown">
                  {canAccessFullAutoCouponsTargeting && (
                    <NavDropdown.Item
                      as={Link}
                      to="/kindred/full-auto-coupons-targeting"
                    >
                      Full Auto Coupons Targeting
                    </NavDropdown.Item>
                  )}
                  {canAccessKindredData && (
                    <>
                      {canAccessFullAutoCouponsTargeting && (
                        <NavDropdown.Divider />
                      )}
                      <NavDropdown.Item as={Link} to="/kindred/samsung">
                        Samsung
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/kindred/deals">
                        Deals
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/kindred/coupons">
                        Coupons
                      </NavDropdown.Item>
                      {canAccessCouponBulkUpdate && (
                        <NavDropdown.Item
                          as={Link}
                          to="/kindred/coupon-bulk-updates"
                        >
                          Coupon Bulk Updates
                        </NavDropdown.Item>
                      )}
                      {canAccessDomainReview && (
                        <NavDropdown.Item as={Link} to="/kindred/domain-review">
                          Domain Review
                        </NavDropdown.Item>
                      )}
                      <NavDropdown.Item as={Link} to="/kindred/brands">
                        Brands
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/kindred/change-log">
                        Change Log
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/kindred/brand-domains">
                        Brand Domains
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/kindred/non-agentic">
                        Non-Agentic Merchants
                      </NavDropdown.Item>
                    </>
                  )}
                  {canAccessQaLists && (
                    <>
                      {(canAccessKindredData ||
                        canAccessFullAutoCouponsTargeting) && (
                        <NavDropdown.Divider />
                      )}
                      <NavDropdown.Item as={Link} to="/qa-lists">
                        QA Lists
                      </NavDropdown.Item>
                    </>
                  )}
                  {canAccessWorkPipeline && (
                    <NavDropdown.Item as={Link} to="/work-pipeline">
                      Work Pipeline
                    </NavDropdown.Item>
                  )}
                </NavDropdown>
              )}
              {canAccessOpenData && (
                <NavDropdown title="Open Data" id="open-data-dropdown">
                  <NavDropdown.Item as={Link} to="/open-data/validation">
                    Validation
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/open-data/readiness">
                    Readiness
                  </NavDropdown.Item>
                </NavDropdown>
              )}
              {canAccessSmartShopping && (
                <NavDropdown title="Smart Shopping" id="smart-shopping-dropdown">
                  <NavDropdown.Item as={Link} to="/smart-shopping/sessions">
                    Sessions
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/smart-shopping/access-codes">
                    Invite codes
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/smart-shopping/product-misses">
                    Product misses
                  </NavDropdown.Item>
                </NavDropdown>
              )}
              {canAccessPartners && (
                <NavDropdown title="Partners" id="partners-dropdown">
                  <NavDropdown.Item as={Link} to="/partners/configuration">
                    Configuration
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/partners/configuration/history">
                    Configuration history
                  </NavDropdown.Item>
                </NavDropdown>
              )}
              {(canAccessAffiliateProducts || canAccessBasketTesting) && (
                <NavDropdown
                  title="Affiliate Products"
                  id="affiliate-products-dropdown"
                >
                  {canAccessAffiliateProducts && (
                    <>
                      <NavDropdown.Item
                        as={Link}
                        to="/affiliate-products/sync-configs"
                      >
                        Sync Configs
                      </NavDropdown.Item>
                      <NavDropdown.Item
                        as={Link}
                        to="/affiliate-products/discovery"
                      >
                        Discovery
                      </NavDropdown.Item>
                      <NavDropdown.Divider />
                      <NavDropdown.Item
                        as={Link}
                        to="/affiliate-products/categories"
                      >
                        Categories
                      </NavDropdown.Item>
                      <NavDropdown.Item
                        as={Link}
                        to="/affiliate-products/products"
                      >
                        Products
                      </NavDropdown.Item>
                      <NavDropdown.Divider />
                      <NavDropdown.Item
                        as={Link}
                        to="/affiliate-products/merchant-review"
                      >
                        Merchant Review
                      </NavDropdown.Item>
                    </>
                  )}
                  {canAccessBasketTesting && (
                    <>
                      {canAccessAffiliateProducts && <NavDropdown.Divider />}
                      <NavDropdown.Item
                        as={Link}
                        to="/affiliate-products/basket-tests"
                      >
                        Basket Testing
                      </NavDropdown.Item>
                    </>
                  )}
                </NavDropdown>
              )}
              {(canManageGroups || canManageUsers) && (
                <NavDropdown title="Admin" id="admin-dropdown">
                  {canManageGroups && (
                    <NavDropdown.Item as={Link} to="/admin/groups">
                      Groups &amp; Permissions
                    </NavDropdown.Item>
                  )}
                  {canManageUsers && (
                    <NavDropdown.Item as={Link} to="/admin/users">
                      Users
                    </NavDropdown.Item>
                  )}
                </NavDropdown>
              )}
              {/* <NavDropdown title="Configure" id="merchants-dropdown">
                <NavDropdown.Item as={Link} to="/merchants">
                  Merchants
                </NavDropdown.Item>
                <NavDropdown.Divider />
                <NavDropdown.Item as={Link} to="/sessions">
                  Sessions
                </NavDropdown.Item>
              </NavDropdown> */}
              {/* <Nav.Item>
                <StyledNavLink className="nav-link" to="/state-machine">
                  State Machine
                </StyledNavLink>
              </Nav.Item> */}
            </Nav>
            <Nav className="ms-auto">
              <Nav.Item>
                <LogoutLink className="nav-link" onClick={handleLogout}>
                  <LogOut size={16} />
                  <span>Logout</span>
                </LogoutLink>
              </Nav.Item>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </StyledNavbar>
      <Outlet />
    </Container>
  );
};
